import { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Eye, Edit, Archive, Calendar, User, FolderOpen, RotateCcw, Download, Upload } from 'lucide-react';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import CreateReportModal from '../components/features/ReportManagement/CreateReportModal';
import EditReportModal from '../components/features/ReportManagement/EditReportModal';
import ViewReportModal from '../components/features/ReportManagement/ViewReportModal';
import ResendReportModal from '../components/features/ReportManagement/ResendReportModal';
import ImportCsvModal from '../components/features/ReportManagement/ImportCsvModal';
import { getReports, archiveReport } from '../services/reportServiceSwitch';
import { exportReportsCsv } from '../services/reportService';
import { getCategories } from '../services/categoryService';
import { useAuth } from '../context/AuthContext';
import './Reports.css';

export default function Reports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [deliveryFilter, setDeliveryFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [toast, setToast] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const isReportManager = user?.role === 'manager' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  const [categories, setCategories] = useState([]);

  // Fetch categories once on mount
  useEffect(() => {
    getCategories()
      .then(cats => setCategories(cats))
      .catch(() => setCategories([]));
  }, []);

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getReports({
        search: searchTerm,
        category: categoryFilter,
        deliveryMethod: deliveryFilter,
        sortBy, sortOrder,
        page: currentPage,
        limit: itemsPerPage
      });
      setReports(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, categoryFilter, deliveryFilter, sortBy, sortOrder, currentPage]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleSearch = (e) => {
    const value = typeof e === 'string' ? e : e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (e) => { setCategoryFilter(e.target.value); setCurrentPage(1); };
  const handleDeliveryChange = (e) => { setDeliveryFilter(e.target.value); setCurrentPage(1); };
  const handleSort = (column, direction) => {
    setSortBy(column);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'name', label: 'Report Name', sortable: true,
      render: (report) => (
        <div className="report-name-cell"><FileText size={18} /><span>{report.name}</span></div>
      )
    },
    { key: 'description', label: 'Description', sortable: false },
    {
      key: 'category', label: 'Category', sortable: true,
      render: (report) => <span className="category-badge">{report.category}</span>
    },
    {
      key: 'deliveryMethod', label: 'Delivery', sortable: true,
      render: (report) => (
        <span className={`delivery-badge ${report.deliveryMethod === 'SFTP' ? 'delivery-sftp' : report.deliveryMethod === 'SFTP_PICKUP' ? 'delivery-pickup' : 'delivery-url'}`}>
          {report.deliveryMethod === 'SFTP' ? 'SFTP' : report.deliveryMethod === 'SFTP_PICKUP' ? 'Pickup' : 'Signed URL'}
        </span>
      )
    },
    {
      key: 'lastUpdated', label: 'Last Updated', sortable: true,
      render: (report) => (
        <div className="date-cell"><Calendar size={16} /><span>{report.lastUpdated ? new Date(report.lastUpdated).toLocaleDateString() : '-'}</span></div>
      )
    },
    {
      key: 'createdBy', label: 'Created By', sortable: false,
      render: (report) => (
        <div className="user-cell"><User size={16} /><span>{report.createdBy || '-'}</span></div>
      )
    },
    {
      key: 'actions', label: 'Actions', sortable: false,
      render: (report) => (
        <div className="action-buttons">
          <button className="action-btn view-btn" onClick={() => handleViewReport(report)} title="View details"><Eye size={16} /></button>
          {isReportManager && (
            <>
              <button className="action-btn edit-btn" onClick={() => handleEditReport(report)} title="Edit report"><Edit size={16} /></button>
              <button className="action-btn archive-btn" onClick={() => handleArchiveReport(report)} title="Archive report"><Archive size={16} /></button>
              <button className="action-btn resend-btn" onClick={() => handleResendReport(report)} title="Resend report"><RotateCcw size={16} /></button>
            </>
          )}
        </div>
      )
    }
  ];

  const handleViewReport = (report) => { setSelectedReport(report); setShowViewModal(true); };
  const handleEditReport = (report) => { setSelectedReport(report); setShowEditModal(true); };
  const handleResendReport = (report) => { setSelectedReport(report); setShowResendModal(true); };
  const handleArchiveReport = async (report) => {
    if (!window.confirm(`Are you sure you want to archive "${report.name}"?`)) return;
    try {
      await archiveReport(report.id);
      showToast('Report archived successfully', 'success');
      fetchReports();
    } catch (err) {
      showToast(err.message || 'Failed to archive report', 'error');
    }
  };

  const showToast = (message, type = 'success') => setToast({ message, type });
  const handleSuccess = (message) => { showToast(message, 'success'); fetchReports(); };

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await exportReportsCsv();
      showToast('CSV exported successfully', 'success');
    } catch {
      showToast('Failed to export CSV. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportSuccess = (result) => {
    const count = result?.created_count || 0;
    showToast(`Successfully imported ${count} report${count !== 1 ? 's' : ''}`, 'success');
    fetchReports();
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><FolderOpen size={32} /> Reports</h1>
            <p className="page-subtitle">Manage and view your BOXI reports</p>
          </div>
          {isReportManager && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)} icon={<Plus size={20} />}>Create Report</Button>
          )}
          {isAdmin && (
            <>
              <Button variant="secondary" onClick={handleExportCsv} loading={exportLoading} disabled={exportLoading} icon={<Download size={18} />}>Export CSV</Button>
              <Button variant="secondary" onClick={() => setShowImportModal(true)} icon={<Upload size={18} />}>Import CSV</Button>
            </>
          )}
        </div>
      </div>

      <Card className="reports-card">
        <div className="reports-controls">
          <SearchBar value={searchTerm} onChange={handleSearch} placeholder="Search reports by name or description..." />
          <div className="filter-controls">
            <select className="category-filter" value={categoryFilter} onChange={handleCategoryChange}>
              <option value="">All Categories</option>
              {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
            <select className="category-filter" value={deliveryFilter} onChange={handleDeliveryChange}>
              <option value="">All Delivery</option>
              <option value="SIGNED_URL">Signed URL</option>
              <option value="SFTP">SFTP</option>
              <option value="SFTP_PICKUP">SFTP Pickup</option>
            </select>
          </div>
        </div>

        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={() => window.location.reload()} />}

        {!loading && !error && (
          <>
            <Table columns={columns} data={reports} sortColumn={sortBy} sortDirection={sortOrder} onSort={handleSort} emptyMessage="No reports found" />
            {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
          </>
        )}
      </Card>

      <CreateReportModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={handleSuccess} />
      <EditReportModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedReport(null); }} report={selectedReport} onSuccess={handleSuccess} />
      <ViewReportModal isOpen={showViewModal} onClose={() => { setShowViewModal(false); setSelectedReport(null); }} report={selectedReport} />
      <ResendReportModal isOpen={showResendModal} onClose={() => { setShowResendModal(false); setSelectedReport(null); }} report={selectedReport} onSuccess={handleSuccess} />
      <ImportCsvModal isOpen={showImportModal} onClose={() => setShowImportModal(false)} onSuccess={handleImportSuccess} />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
